/**
 * Unified Object Browser Component
 *
 * Combines features from both the code editor's ObjectBrowser and the visual editor's ObjectBrowser.
 * Supports hierarchical navigation, context menus, validation status, and both code/visual editing modes.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useVisualObjectEditorStore } from '@/visual-object-editor/visualObjectEditorStore';
import { useIDFStore } from '@/stores/idfStore';
import { useIDDStore } from '@/stores/iddStore';
import { useEditorCommands, useEditorReady } from '@/editor/EditorContext';
import { calculateValidationSummary } from '@/visual-object-editor/validation';
import type { ObjectTreeNode, ValidationStatus } from '@/visual-object-editor/types';

/**
 * View mode for the unified editor
 */
type EditorViewMode = 'code' | 'cards' | 'table';

/**
 * Unified Object Browser Props
 */
interface UnifiedObjectBrowserProps {
  viewMode: EditorViewMode;
  onNewObject?: () => void;
  onObjectSelect?: (id: string, multi: boolean) => void;
}

/**
 * Validation status icon
 */
function ValidationIcon({ status }: { status: ValidationStatus }) {
  switch (status) {
    case 'valid':
      return (
        <svg className="h-3.5 w-3.5 text-green-500" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
        </svg>
      );
    case 'warning':
      return (
        <svg className="h-3.5 w-3.5 text-amber-500" viewBox="0 0 24 24" fill="currentColor">
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
        </svg>
      );
    case 'error':
      return (
        <svg className="h-3.5 w-3.5 text-red-500" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
        </svg>
      );
  }
}

/**
 * Chevron icon for expandable nodes
 */
function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={`h-4 w-4 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`}
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
    </svg>
  );
}

/**
 * Context menu state
 */
interface ContextMenuState {
  isOpen: boolean;
  x: number;
  y: number;
  className: string;
  objectId?: string;
}

/**
 * Context menu item component
 */
function ContextMenuItem({
  children,
  onClick,
  destructive = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      className={`w-full px-3 py-1.5 text-left text-sm transition-colors ${
        destructive
          ? 'text-destructive hover:bg-destructive/10'
          : 'hover:bg-secondary'
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * Validation summary bar
 */
function ValidationSummary({
  valid,
  warnings,
  errors,
}: {
  valid: number;
  warnings: number;
  errors: number;
}) {
  const total = valid + warnings + errors;
  if (total === 0) return null;

  return (
    <div className="border-t bg-muted/50 px-3 py-2">
      <div className="flex items-center gap-3 text-xs">
        <div className="flex items-center gap-1 text-green-600">
          <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
          <span>{valid}</span>
        </div>
        <div className="flex items-center gap-1 text-amber-600">
          <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
          </svg>
          <span>{warnings}</span>
        </div>
        <div className="flex items-center gap-1 text-red-600">
          <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <span>{errors}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Filter dropdown
 */
function FilterDropdown({
  groups,
  filter,
  onFilterChange,
}: {
  groups: string[];
  filter: { groupFilter: string | null; showOnlyInvalid: boolean; showOnlyWarnings: boolean };
  onFilterChange: (update: Partial<typeof filter>) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const hasActiveFilters =
    filter.groupFilter !== null ||
    filter.showOnlyInvalid ||
    filter.showOnlyWarnings;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`rounded p-1.5 ${hasActiveFilters ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
        title="Filter options"
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full z-20 mt-1 w-56 rounded-md border bg-popover shadow-lg">
            <div className="p-3 space-y-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Group
                </label>
                <select
                  value={filter.groupFilter || ''}
                  onChange={(e) => onFilterChange({ groupFilter: e.target.value || null })}
                  className="w-full rounded border px-2 py-1 text-sm bg-background"
                >
                  <option value="">All Groups</option>
                  {groups.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={filter.showOnlyInvalid}
                    onChange={(e) => onFilterChange({ showOnlyInvalid: e.target.checked })}
                    className="rounded border-border"
                  />
                  Show only errors
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={filter.showOnlyWarnings}
                    onChange={(e) => onFilterChange({ showOnlyWarnings: e.target.checked })}
                    className="rounded border-border"
                  />
                  Show only warnings
                </label>
              </div>

              {hasActiveFilters && (
                <button
                  onClick={() => onFilterChange({
                    groupFilter: null,
                    showOnlyInvalid: false,
                    showOnlyWarnings: false,
                  })}
                  className="text-xs text-primary hover:underline"
                >
                  Clear all filters
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Unified Object Browser Component
 */
export function UnifiedObjectBrowser({
  viewMode,
  onNewObject,
  onObjectSelect,
}: UnifiedObjectBrowserProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Store state
  const idfDocument = useIDFStore((state) => state.document);
  const isParsed = useIDFStore((state) => state.isParsed);
  const getObjectsByClass = useIDFStore((state) => state.getObjectsByClass);
  const removeObject = useIDFStore((state) => state.removeObject);
  const duplicateObject = useIDFStore((state) => state.duplicateObject);
  const parseContent = useIDFStore((state) => state.parseContent);
  const serialize = useIDFStore((state) => state.serialize);

  const schema = useIDDStore((state) => state.schema);
  const isSchemaLoaded = useIDDStore((state) => state.isLoaded);
  const isSchemaLoading = useIDDStore((state) => state.isLoading);
  const schemaErrors = useIDDStore((state) => state.errors);
  const loadSchemaFromUrl = useIDDStore((state) => state.loadSchemaFromUrl);

  const selectedObjectIds = useVisualObjectEditorStore((state) => state.selectedObjectIds);
  const expandedGroups = useVisualObjectEditorStore((state) => state.expandedGroups);
  const expandedClasses = useVisualObjectEditorStore((state) => state.expandedClasses);
  const filter = useVisualObjectEditorStore((state) => state.filter);
  const selectObject = useVisualObjectEditorStore((state) => state.selectObject);
  const toggleGroup = useVisualObjectEditorStore((state) => state.toggleGroup);
  const toggleClass = useVisualObjectEditorStore((state) => state.toggleClass);
  const setFilter = useVisualObjectEditorStore((state) => state.setFilter);
  const buildObjectTree = useVisualObjectEditorStore((state) => state.buildObjectTree);

  // Editor commands (for code view)
  const commands = useEditorCommands();
  const isEditorReady = useEditorReady();

  // Context menu state
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    isOpen: false,
    x: 0,
    y: 0,
    className: '',
  });

  // Close context menu when clicking outside
  useEffect(() => {
    if (!contextMenu.isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenu((prev) => ({ ...prev, isOpen: false }));
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenu.isOpen]);

  // Cleanup navigation timeout on unmount
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  // Build object tree
  const objectTree = useMemo(() => {
    if (!idfDocument || !isSchemaLoaded) return [];
    return buildObjectTree(idfDocument, schema);
  }, [idfDocument, schema, isSchemaLoaded, buildObjectTree]);

  // Get all group names for filter
  const groupNames = useMemo(() => objectTree.map((g) => g.name), [objectTree]);

  // Filter tree by search query
  const filteredTree = useMemo((): ObjectTreeNode[] => {
    if (!searchQuery.trim()) return objectTree;
    const query = searchQuery.toLowerCase();
    const result: ObjectTreeNode[] = [];

    for (const group of objectTree) {
      const filteredChildren: ObjectTreeNode[] = [];
      for (const classNode of group.children || []) {
        // Match class name or object names
        const classMatches = classNode.name.toLowerCase().includes(query);
        const classChildren = classNode.children || [];
        const filteredObjects = classChildren.filter((obj) =>
          obj.name.toLowerCase().includes(query) || classMatches
        );
        if (filteredObjects.length === 0 && !classMatches) continue;
        filteredChildren.push({
          ...classNode,
          children: classMatches ? classChildren : filteredObjects,
          objectCount: classMatches ? (classNode.objectCount ?? 0) : filteredObjects.length,
        });
      }
      if (filteredChildren.length === 0) continue;
      result.push({
        ...group,
        children: filteredChildren,
        objectCount: filteredChildren.reduce((sum, c) => sum + (c.objectCount || 0), 0),
      });
    }
    return result;
  }, [objectTree, searchQuery]);

  // Calculate validation summary
  const validationSummary = useMemo(() => {
    if (!idfDocument || !isSchemaLoaded) return { valid: 0, warnings: 0, errors: 0 };
    return calculateValidationSummary(idfDocument, schema);
  }, [idfDocument, schema, isSchemaLoaded]);

  // Handle object click
  const handleObjectClick = useCallback(
    (objectId: string, multi: boolean) => {
      // Update selection in visual store
      selectObject(objectId, multi);

      // Notify parent
      onObjectSelect?.(objectId, multi);

      // If in code view, navigate to the object
      if (viewMode === 'code' && isEditorReady) {
        commands.navigateToObject(objectId);
      }
    },
    [selectObject, onObjectSelect, viewMode, isEditorReady, commands]
  );

  // Handle class click - filter to show objects of this class
  const handleClassClick = useCallback(
    (className: string) => {
      setFilter({ classFilter: className });
    },
    [setFilter]
  );

  // Handle right click context menu
  const handleContextMenu = useCallback(
    (event: React.MouseEvent, className: string, objectId?: string) => {
      event.preventDefault();
      const menuState: ContextMenuState = {
        isOpen: true,
        x: event.clientX,
        y: event.clientY,
        className,
      };
      if (objectId) {
        menuState.objectId = objectId;
      }
      setContextMenu(menuState);
    },
    []
  );

  // Context menu actions (code view specific)
  const handleSelectAll = useCallback(() => {
    if (viewMode === 'code') {
      commands.selectAllOfClass(contextMenu.className);
    } else {
      // Select all objects of this class
      const objects = getObjectsByClass(contextMenu.className);
      objects.forEach((obj) => selectObject(obj.id, true));
    }
    setContextMenu((prev) => ({ ...prev, isOpen: false }));
  }, [viewMode, commands, contextMenu.className, getObjectsByClass, selectObject]);

  const handleCollapseAll = useCallback(() => {
    commands.foldAllOfClass(contextMenu.className);
    setContextMenu((prev) => ({ ...prev, isOpen: false }));
  }, [commands, contextMenu.className]);

  const handleExpandAll = useCallback(() => {
    commands.unfoldAllOfClass(contextMenu.className);
    setContextMenu((prev) => ({ ...prev, isOpen: false }));
  }, [commands, contextMenu.className]);

  const handleCopyAll = useCallback(async () => {
    await commands.copyAllOfClass(contextMenu.className);
    setContextMenu((prev) => ({ ...prev, isOpen: false }));
  }, [commands, contextMenu.className]);

  const handleHighlightClass = useCallback(() => {
    commands.highlightClass(contextMenu.className);
    setContextMenu((prev) => ({ ...prev, isOpen: false }));
  }, [commands, contextMenu.className]);

  const handleDimOthers = useCallback(() => {
    commands.dimOtherClasses(contextMenu.className);
    setContextMenu((prev) => ({ ...prev, isOpen: false }));
  }, [commands, contextMenu.className]);

  const handleDuplicateFirst = useCallback(() => {
    const objects = getObjectsByClass(contextMenu.className);
    const firstObject = objects[0];
    if (!firstObject) return;

    const newId = duplicateObject(firstObject.id);
    if (newId) {
      const newContent = serialize();
      parseContent(newContent);
      if (viewMode === 'code') {
        // Clear any existing timeout before setting a new one
        if (navigationTimeoutRef.current) {
          clearTimeout(navigationTimeoutRef.current);
        }
        navigationTimeoutRef.current = setTimeout(() => commands.navigateToObject(newId), 100);
      }
    }
    setContextMenu((prev) => ({ ...prev, isOpen: false }));
  }, [contextMenu.className, getObjectsByClass, duplicateObject, serialize, parseContent, viewMode, commands]);

  const handleDeleteAll = useCallback(() => {
    const objects = getObjectsByClass(contextMenu.className);
    if (objects.length === 0) return;

    if (window.confirm(`Delete all ${objects.length} "${contextMenu.className}" objects?`)) {
      for (const obj of objects) {
        removeObject(obj.id);
      }
      const newContent = serialize();
      parseContent(newContent);
    }
    setContextMenu((prev) => ({ ...prev, isOpen: false }));
  }, [contextMenu.className, getObjectsByClass, removeObject, serialize, parseContent]);

  const objectCount = idfDocument?.objects.length ?? 0;

  if (!isParsed || !idfDocument) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b px-4 py-2">
          <h2 className="text-sm font-semibold">Objects</h2>
        </div>
        <div className="p-4 text-sm text-muted-foreground">
          Load an IDF file to see objects
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col min-h-0">
      {/* Header */}
      <div className="border-b px-3 py-2 flex-shrink-0">
        <h2 className="text-sm font-semibold">Objects</h2>
        <p className="text-xs text-muted-foreground">
          {objectCount} object{objectCount !== 1 ? 's' : ''} in model
        </p>
      </div>

      {/* Search and filter */}
      <div className="border-b p-2 flex-shrink-0">
        <div className="flex gap-1">
          <div className="relative flex-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search objects..."
              className="w-full rounded-md border py-1.5 pl-8 pr-3 text-sm bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <svg
              className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
                </svg>
              </button>
            )}
          </div>
          <FilterDropdown
            groups={groupNames}
            filter={{
              groupFilter: filter.groupFilter,
              showOnlyInvalid: filter.showOnlyInvalid,
              showOnlyWarnings: filter.showOnlyWarnings,
            }}
            onFilterChange={setFilter}
          />
        </div>
      </div>

      {/* Clear class filter indicator */}
      {filter.classFilter && (
        <div className="flex items-center justify-between border-b bg-primary/10 px-3 py-1.5 text-xs">
          <span>
            Filtered: <strong>{filter.classFilter}</strong>
          </span>
          <button
            onClick={() => setFilter({ classFilter: null })}
            className="text-primary hover:underline"
          >
            Clear
          </button>
        </div>
      )}

      {/* Object tree */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {isSchemaLoading ? (
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <svg className="h-12 w-12 animate-spin text-muted-foreground/50" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="mt-2 text-sm text-muted-foreground">Loading schema...</p>
          </div>
        ) : !isSchemaLoaded && schemaErrors.length > 0 ? (
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <svg className="h-12 w-12 text-destructive/50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="mt-2 text-sm text-muted-foreground">Schema load failed</p>
            <button
              onClick={() => loadSchemaFromUrl('/energyplus/Energy+.idd')}
              className="mt-2 text-xs text-primary hover:underline"
            >
              Retry
            </button>
          </div>
        ) : !isSchemaLoaded ? (
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <svg className="h-12 w-12 animate-spin text-muted-foreground/50" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="mt-2 text-sm text-muted-foreground">Loading schema...</p>
          </div>
        ) : filteredTree.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <svg className="h-12 w-12 text-muted-foreground/50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="mt-2 text-sm text-muted-foreground">
              {searchQuery ? 'No matching objects' : 'No objects in document'}
            </p>
          </div>
        ) : (
          <div>
            {filteredTree.map((group) => {
              const isGroupExpanded = expandedGroups instanceof Set ? expandedGroups.has(group.name) : false;

              return (
                <div key={group.id} className="border-b border-border/50 last:border-b-0">
                  {/* Group header */}
                  <button
                    onClick={() => toggleGroup(group.name)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/50"
                  >
                    <ChevronIcon expanded={isGroupExpanded} />
                    <svg className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
                    </svg>
                    <span className="flex-1 truncate text-sm font-medium">{group.name}</span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <span>{group.objectCount || 0}</span>
                      {group.validationStatus !== 'valid' && (
                        <ValidationIcon status={group.validationStatus} />
                      )}
                    </span>
                  </button>

                  {/* Classes within group */}
                  {isGroupExpanded && group.children?.map((classNode) => {
                    const isClassExpanded = expandedClasses instanceof Set ? expandedClasses.has(classNode.name) : false;

                    return (
                      <div key={classNode.id} className="ml-4 border-l border-border/50">
                        {/* Class header */}
                        <button
                          onClick={() => toggleClass(classNode.name)}
                          onContextMenu={(e) => handleContextMenu(e, classNode.name)}
                          onDoubleClick={() => handleClassClick(classNode.name)}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/50"
                          title="Click to expand, double-click to filter, right-click for options"
                        >
                          <ChevronIcon expanded={isClassExpanded} />
                          <span className="flex-1 truncate text-sm">{classNode.name}</span>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <span>{classNode.objectCount || 0}</span>
                            {classNode.validationStatus !== 'valid' && (
                              <ValidationIcon status={classNode.validationStatus} />
                            )}
                          </span>
                        </button>

                        {/* Objects within class */}
                        {isClassExpanded && classNode.children && (
                          <div className="ml-3 border-l border-border/30">
                            {classNode.children.map((objectNode) => {
                              const isSelected = selectedObjectIds.includes(objectNode.id);

                              return (
                                <button
                                  key={objectNode.id}
                                  onClick={(e) => handleObjectClick(objectNode.id, e.ctrlKey || e.metaKey)}
                                  onContextMenu={(e) => handleContextMenu(e, classNode.name, objectNode.id)}
                                  className={`flex w-full items-center gap-2 px-3 py-1 text-left transition-colors ${
                                    isSelected
                                      ? 'bg-primary/10 text-primary'
                                      : 'hover:bg-muted/50'
                                  }`}
                                >
                                  <ValidationIcon status={objectNode.validationStatus} />
                                  <span className="flex-1 truncate text-sm">{objectNode.name}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Validation summary */}
      <div className="flex-shrink-0">
        <ValidationSummary {...validationSummary} />
      </div>

      {/* Action buttons */}
      <div className="border-t p-2 flex-shrink-0">
        <button
          onClick={onNewObject}
          className="flex w-full items-center justify-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
          </svg>
          Add Object
        </button>
      </div>

      {/* Context Menu */}
      {contextMenu.isOpen && (
        <div
          ref={contextMenuRef}
          className="fixed z-50 min-w-48 rounded-md border bg-popover py-1 shadow-lg"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <div className="border-b px-3 py-1.5 text-xs font-semibold text-muted-foreground">
            {contextMenu.className}
          </div>

          {/* Selection */}
          <div className="border-b py-1">
            <ContextMenuItem onClick={handleSelectAll}>
              Select All of Type
            </ContextMenuItem>
          </div>

          {/* Code view specific actions */}
          {viewMode === 'code' && (
            <>
              <div className="border-b py-1">
                <ContextMenuItem onClick={handleCollapseAll}>Collapse All</ContextMenuItem>
                <ContextMenuItem onClick={handleExpandAll}>Expand All</ContextMenuItem>
              </div>

              <div className="border-b py-1">
                <ContextMenuItem onClick={handleHighlightClass}>Highlight All</ContextMenuItem>
                <ContextMenuItem onClick={handleDimOthers}>Focus (Dim Others)</ContextMenuItem>
              </div>
            </>
          )}

          {/* Clipboard & Editing */}
          <div className="border-b py-1">
            {viewMode === 'code' && (
              <ContextMenuItem onClick={handleCopyAll}>Copy All to Clipboard</ContextMenuItem>
            )}
            <ContextMenuItem onClick={handleDuplicateFirst}>Duplicate First</ContextMenuItem>
          </div>

          {/* Destructive */}
          <div className="py-1">
            <ContextMenuItem onClick={handleDeleteAll} destructive>
              Delete All ({getObjectsByClass(contextMenu.className).length})
            </ContextMenuItem>
          </div>
        </div>
      )}
    </div>
  );
}
